/**
 * Permissions Configuration — UAM v2.0
 *
 * Source of truth for role-based access control.
 * Based on PRD_UAM_CustomRoles2.md §3 permission taxonomy.
 *
 * v2.0 changes (Phase 1):
 *  - Removed: dash:view, dash:pay, dash:att, dash:emp (cards now gate on
 *    direct area perms; dashboard route accessible to any authed user).
 *  - Removed: emp:add, emp:deact (collapsed into emp:edit).
 *  - Removed: set:kiosk (kiosk feature is dead code; removal independent
 *    of the kiosk-cleanup follow-up).
 *  - Added: emp:pii:view, emp:pii:edit (PII subcollection access — see
 *    PRD §5.6).
 *  - Net: 34 → 29 permissions.
 *
 * v2.0 Phase 2:
 *  - Renamed: time:ot-approve → time:ot:edit.
 *  - Added: time:ot:view (read-only OT Approval page access).
 *  - Net: 29 → 30 permissions.
 *
 * v2.0 Phase 3:
 *  - Renamed: pay:view → payroll:view, pay:run → payroll:run,
 *    pay:slips → payslip:view. Cleans up the ambiguous `pay:` prefix.
 *  - Predefined Viewer also gains payslip:view (read-only payslip access).
 *  - Net: 30 permissions (pure rename, no count change).
 *
 * v2.0 Phase 5 — Payroll approval workflow:
 *  - Added: payroll:approve (approve / reject pending payroll runs).
 *  - Granted by default to predefined Superuser + Admin (both auto-inherit
 *    via ALL_PERMISSIONS). Not granted to Viewer.
 *  - See PRD_PayrollApproval.md.
 *  - Net: 29 → 30 permissions. JWT impact: +18 B on Superuser claim
 *    (still ~113 B headroom to the 800 B warn threshold).
 *
 * Kiosk Time Clock — kiosk management permissions:
 *  - Added: kiosk:view (read the kiosk list), kiosk:edit (create / rename /
 *    re-pair / revoke kiosks). Page is a top-level item under Time & Attendance,
 *    below OT Approval (its own resource, not a set:* settings perm — C16).
 *    Reintroduces a kiosk permission (the old set:kiosk was removed as dead
 *    code in Phase 1) under the v2.0 <area>:<action> convention.
 *  - Superuser + Admin auto-inherit via ALL_PERMISSIONS; Viewer gains
 *    kiosk:view (read-only, like every other :view). Delegatable.
 *  - See PRD_KioskTimeClock.md (C16). Net: 30 → 32 permissions.
 *
 * v2.0 Phase 4 — naming hygiene completes v2.0:
 *  - Renamed: usr:view → set:team:view, usr:edit → set:team:edit,
 *    usr:roles → set:roles:edit, usr:roles:view → set:roles:view.
 *  - Collapsed: usr:add + usr:deact → set:team:edit (per §3.1 banned
 *    suffixes; same pattern as Phase 1's emp:add + emp:deact → emp:edit).
 *  - Settings :edit suffix conformance — set:dept, set:rates, set:att-policy,
 *    set:hol, set:csv, set:slips, set:bill all renamed to add explicit
 *    :edit suffix per §3.1 (no implicit-edit forms).
 *  - Added: set:bill:view as placeholder for future view-only billing
 *    access (Superuser-only, no UI consumer yet).
 *  - Removed dangling hasPermission(set:kiosk) check in AppLayout — the
 *    perm was removed in Phase 1; the check was dead code.
 *  - Net: 30 → 29 permissions. All keys now conform to <area>:view /
 *    <area>:edit / <area>:<scope>:view / <area>:<scope>:edit.
 *
 * Standalone Adjustments — adjustments permissions:
 *  - Added: adj:view (read the Adjustments page), adj:edit (create / edit /
 *    void adjustment entries). Own area per the <area>:<action> convention —
 *    adjustments are a first-class resource (companies/{cid}/adjustments),
 *    separate from running payroll, so an HR/ops role can log a cash advance
 *    without holding payroll:run (segregation of duties).
 *  - Superuser + Admin auto-inherit via ALL_PERMISSIONS; Viewer gains
 *    adj:view (read-only, like every other :view). Delegatable.
 *  - See PRD_StandaloneAdjustments.md (C6). Net: 32 → 34 permissions.
 *
 * Work Schedule Management — work-schedule permissions:
 *  - Added: sched:view (read the Work Schedule page), sched:edit (edit /
 *    bulk-assign employees' work schedules). Own area under the Employees
 *    nav section. The write lands on the employee doc, but a field-scoped
 *    rules clause (canScheduleOnlyUpdate) admits a sched:edit holder to
 *    change ONLY employment.workSchedule — a scheduler role needs no
 *    emp:edit (segregation of duties).
 *  - Superuser + Admin auto-inherit via ALL_PERMISSIONS; Viewer gains
 *    sched:view (read-only, like every other :view). Delegatable.
 *  - See PRD_WorkScheduleManagement.md (C1, C9). Net: 34 → 36 permissions.
 *
 * Loans Management — loan permissions:
 *  - Added: loan:view (read the Loans Management page), loan:edit
 *    (create / edit / pause / close / delete loans). Own
 *    area under the Employees nav section; a first-class resource
 *    (companies/{cid}/loans) whose ledger fields are approve-only in rules,
 *    so a loans clerk needs neither emp:edit nor payroll:run.
 *  - Superuser + Admin auto-inherit via ALL_PERMISSIONS; Viewer gains
 *    loan:view (read-only, like every other :view). Delegatable.
 *  - See PRD_LoansManagement.md (C11). Net: 36 → 38 permissions.
 *
 * Firebase JWT custom-claims hard limit is 1000 bytes. The parity script
 * (scripts/check-permissions-parity.js) gates CI at 80% (800 B). 38 keys:
 * 785 B worst-case against the 800 B gate — this was the LAST view/edit pair
 * the current claim shape can take; the next one needs the claim shape
 * redesigned (or keys shortened) first (PRD_LoansManagement.md C11).
 */

// ============================================================
// PERMISSION KEYS
// ============================================================

export const PERMISSIONS = {
  // Employees
  EMPLOYEES_VIEW: 'emp:view',
  EMPLOYEES_EDIT: 'emp:edit',
  EMPLOYEES_PII_VIEW: 'emp:pii:view',
  EMPLOYEES_PII_EDIT: 'emp:pii:edit',

  // Work Schedule (own area under the Employees nav section; the employee-doc
  // write is field-scoped by canScheduleOnlyUpdate — PRD_WorkScheduleManagement.md C1)
  SCHEDULE_VIEW: 'sched:view',
  SCHEDULE_EDIT: 'sched:edit',

  // Loans (own area under the Employees nav section — companies/{cid}/loans;
  // see PRD_LoansManagement.md C11)
  LOANS_VIEW: 'loan:view',
  LOANS_EDIT: 'loan:edit',

  // Time & Attendance
  TIME_TRACKING_VIEW: 'time:view',
  TIME_TRACKING_EDIT: 'time:edit',
  TIME_OT_VIEW: 'time:ot:view',
  TIME_OT_EDIT: 'time:ot:edit',

  // Payroll
  PAYROLL_VIEW: 'payroll:view',
  PAYROLL_RUN: 'payroll:run',
  PAYROLL_APPROVE: 'payroll:approve',
  PAYSLIP_VIEW: 'payslip:view',

  // Adjustments (own area — standalone resource under the Payroll nav section;
  // see PRD_StandaloneAdjustments.md C6)
  ADJUSTMENTS_VIEW: 'adj:view',
  ADJUSTMENTS_EDIT: 'adj:edit',

  // Settings — Company
  SETTINGS_DEPARTMENTS: 'set:dept:edit',
  SETTINGS_DEPARTMENTS_VIEW: 'set:dept:view',
  SETTINGS_TEAM_EDIT: 'set:team:edit',
  SETTINGS_TEAM_VIEW: 'set:team:view',
  SETTINGS_ROLES_EDIT: 'set:roles:edit',
  SETTINGS_ROLES_VIEW: 'set:roles:view',
  SETTINGS_ATTENDANCE_POLICY: 'set:att-policy:edit',
  SETTINGS_ATTENDANCE_POLICY_VIEW: 'set:att-policy:view',
  SETTINGS_HOLIDAYS: 'set:hol:edit',
  SETTINGS_HOLIDAYS_VIEW: 'set:hol:view',

  // Kiosks (top-level under Time & Attendance; own resource per the v2.0 area:action convention)
  KIOSK_VIEW: 'kiosk:view',
  KIOSK_EDIT: 'kiosk:edit',

  // Settings — Payroll
  SETTINGS_PAYROLL_RATES: 'set:rates:edit',
  SETTINGS_PAYROLL_RATES_VIEW: 'set:rates:view',
  SETTINGS_CSV_EXPORT: 'set:csv:edit',
  SETTINGS_CSV_EXPORT_VIEW: 'set:csv:view',
  SETTINGS_PAYSLIPS: 'set:slips:edit',
  SETTINGS_PAYSLIPS_VIEW: 'set:slips:view',

  // Settings — Billing (Superuser only; both non-delegatable)
  SETTINGS_PAYMENTS: 'set:bill:edit',
  SETTINGS_PAYMENTS_VIEW: 'set:bill:view',
};

/** Flat array of all permission values */
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);

/**
 * Permissions that MAY NOT appear in any custom role.
 * See PRD_UAM_CustomRoles.md §4.2 Rule 1.
 *
 * The set is hardcoded (not derived from the Superuser-minus-Admin diff) so
 * adding a new Superuser-only permission remains a deliberate decision.
 *
 * Must stay in sync with functions/src/config/permissions.js.
 */
export const NON_DELEGATABLE_PERMISSIONS = new Set([
  PERMISSIONS.SETTINGS_PAYMENTS,
  PERMISSIONS.SETTINGS_PAYMENTS_VIEW,
]);

// ============================================================
// PERMISSION CATEGORIES (flat metadata for any UI listing perms)
// ============================================================

export const PERMISSION_CATEGORIES = [
  {
    id: 'employees',
    label: 'Employees',
    permissions: [
      { key: PERMISSIONS.EMPLOYEES_VIEW, label: 'View employee list & basic profile' },
      { key: PERMISSIONS.EMPLOYEES_EDIT, label: 'Create / edit / deactivate employees' },
      { key: PERMISSIONS.EMPLOYEES_PII_VIEW, label: 'View PII (compensation, banking, gov IDs)' },
      { key: PERMISSIONS.EMPLOYEES_PII_EDIT, label: 'Edit PII' },
      { key: PERMISSIONS.SCHEDULE_VIEW, label: 'View work schedules' },
      { key: PERMISSIONS.SCHEDULE_EDIT, label: 'Edit and bulk-assign work schedules' },
      { key: PERMISSIONS.LOANS_VIEW, label: 'View loans' },
      { key: PERMISSIONS.LOANS_EDIT, label: 'Create / edit / pause / close loans' },
    ],
  },
  {
    id: 'time-tracking',
    label: 'Time & Attendance',
    permissions: [
      { key: PERMISSIONS.TIME_TRACKING_VIEW, label: 'View attendance records' },
      { key: PERMISSIONS.TIME_TRACKING_EDIT, label: 'Manual corrections & overrides' },
      { key: PERMISSIONS.TIME_OT_VIEW, label: 'View overtime approvals' },
      { key: PERMISSIONS.TIME_OT_EDIT, label: 'Approve/reject overtime' },
      { key: PERMISSIONS.KIOSK_VIEW, label: 'View kiosks' },
      { key: PERMISSIONS.KIOSK_EDIT, label: 'Create / rename / re-pair / revoke kiosks' },
    ],
  },
  {
    id: 'payroll',
    label: 'Payroll',
    permissions: [
      { key: PERMISSIONS.PAYROLL_VIEW, label: 'View payroll runs & pay slips' },
      { key: PERMISSIONS.PAYROLL_RUN, label: 'Create & process payroll runs' },
      { key: PERMISSIONS.PAYROLL_APPROVE, label: 'Approve / reject payroll runs' },
      { key: PERMISSIONS.PAYSLIP_VIEW, label: 'View & download payslips' },
      { key: PERMISSIONS.ADJUSTMENTS_VIEW, label: 'View adjustment entries' },
      { key: PERMISSIONS.ADJUSTMENTS_EDIT, label: 'Create / edit / void adjustment entries' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    permissions: [
      { key: PERMISSIONS.SETTINGS_DEPARTMENTS, label: 'Manage departments' },
      { key: PERMISSIONS.SETTINGS_DEPARTMENTS_VIEW, label: 'View departments' },
      { key: PERMISSIONS.SETTINGS_TEAM_EDIT, label: 'Invite / edit / deactivate team members' },
      { key: PERMISSIONS.SETTINGS_TEAM_VIEW, label: 'View team members' },
      { key: PERMISSIONS.SETTINGS_ROLES_EDIT, label: 'Create / edit / delete custom roles' },
      { key: PERMISSIONS.SETTINGS_ROLES_VIEW, label: 'View custom roles' },
      { key: PERMISSIONS.SETTINGS_ATTENDANCE_POLICY, label: 'Manage attendance policy' },
      { key: PERMISSIONS.SETTINGS_ATTENDANCE_POLICY_VIEW, label: 'View attendance policy' },
      { key: PERMISSIONS.SETTINGS_HOLIDAYS, label: 'Manage holiday calendar' },
      { key: PERMISSIONS.SETTINGS_HOLIDAYS_VIEW, label: 'View holiday calendar' },
      { key: PERMISSIONS.SETTINGS_PAYROLL_RATES, label: 'Edit pay rates & premiums' },
      { key: PERMISSIONS.SETTINGS_PAYROLL_RATES_VIEW, label: 'View pay rates & premiums' },
      { key: PERMISSIONS.SETTINGS_CSV_EXPORT, label: 'Configure CSV export layout' },
      { key: PERMISSIONS.SETTINGS_CSV_EXPORT_VIEW, label: 'View CSV export layout' },
      { key: PERMISSIONS.SETTINGS_PAYSLIPS, label: 'Manage payslip download format settings' },
      { key: PERMISSIONS.SETTINGS_PAYSLIPS_VIEW, label: 'View payslip download format settings' },
      { key: PERMISSIONS.SETTINGS_PAYMENTS, label: 'Manage billing & payments' },
      { key: PERMISSIONS.SETTINGS_PAYMENTS_VIEW, label: 'View billing & payments' },
    ],
  },
];

// Predefined role constants (PREDEFINED_ROLES, getPermissionsForRole,
// roleHasPermission) live in `./roles.js` — see PRD_UAM_CustomRoles.md §11.1.

// ============================================================
// ROUTE-GUARD CONVENTION
// ============================================================
//
// Route guards live on <PermissionGuard> in App.js (the single source of
// truth). Pick the guard by what the page does:
//
//   1. Pure read page (no write controls)        -> permission={X_VIEW}
//        e.g. payroll list/detail, payslip list, employee view
//   2. Dual page (read view + inline write)       -> permissions={[X_EDIT, X_VIEW]}
//        e.g. employees list, time tracking, OT approval, team,
//        and every settings list page. Each write control is then
//        additionally gated on X_EDIT via hasPermission().
//   3. Form/action page (no standalone read view) -> permission={X_EDIT|X_RUN|X_APPROVE}
//        e.g. employees/add, employees/:id/edit, payroll/process,
//        payroll-approval (payroll has no approval-view perm), role editor
//
// Dual pages use OR(:edit, :view) so the route admits both viewers and
// editors explicitly — and stays correct even if an :edit grant ever lacks
// its :view (the matrix enforces :edit ⊇ :view only on the frontend; the
// backend does not). Under the subscription read-only clamp this also makes
// dual pages degrade to read-only views while form pages become inaccessible.
//
// Dashboard intentionally has no guard — accessible to any authenticated user
// (the page renders only the cards/stats the user has direct area perms for;
// an empty dashboard is acceptable UX vs. redirecting away).

// All settings-related permissions (used for parent "Settings" nav visibility)
export const ANY_SETTINGS_PERMISSION = [
  PERMISSIONS.SETTINGS_DEPARTMENTS,
  PERMISSIONS.SETTINGS_DEPARTMENTS_VIEW,
  PERMISSIONS.SETTINGS_TEAM_EDIT,
  PERMISSIONS.SETTINGS_TEAM_VIEW,
  PERMISSIONS.SETTINGS_ROLES_EDIT,
  PERMISSIONS.SETTINGS_ROLES_VIEW,
  PERMISSIONS.SETTINGS_ATTENDANCE_POLICY,
  PERMISSIONS.SETTINGS_ATTENDANCE_POLICY_VIEW,
  PERMISSIONS.SETTINGS_HOLIDAYS,
  PERMISSIONS.SETTINGS_HOLIDAYS_VIEW,
  PERMISSIONS.SETTINGS_PAYROLL_RATES,
  PERMISSIONS.SETTINGS_PAYROLL_RATES_VIEW,
  PERMISSIONS.SETTINGS_CSV_EXPORT,
  PERMISSIONS.SETTINGS_CSV_EXPORT_VIEW,
  PERMISSIONS.SETTINGS_PAYSLIPS,
  PERMISSIONS.SETTINGS_PAYSLIPS_VIEW,
  PERMISSIONS.SETTINGS_PAYMENTS,
  PERMISSIONS.SETTINGS_PAYMENTS_VIEW,
];

// The Reports nav entry and the Reports hub route (App.js) both gate on THIS
// array, any-of, so the menu and the route cannot drift
// (PRD_ContributionBasisReport C6). Each key stands in for one card's full
// `requires`: payroll:view IS the Contribution Basis card's gate, time:view
// stands in for the DTR pair (time:view AND emp:view) because every matrix level
// granting time:view also grants emp:view, and loan:view stands in for the Loan
// Remittance pair (loan:view AND emp:view) on the same premise
// (PRD_LoanRemittanceReport C8) — reportCards.test.js pins both. Never a union
// over ALL card keys: that would include emp:view and show the entry to
// emp:view-only roles who then bounce (PRD_DtrReport C1; narrowed by
// PRD_WorkScheduleManagement C17). Kept in sync with reportCards.js
// by hand: deriving it would be an import cycle (reportCards.js imports
// PERMISSIONS from this file).
export const REPORTS_HUB_ANY = [
  PERMISSIONS.TIME_TRACKING_VIEW,
  PERMISSIONS.PAYROLL_VIEW,
  PERMISSIONS.LOANS_VIEW,
];

// ============================================================
// SIDEBAR NAVIGATION STRUCTURE
// ============================================================
//
// Dashboard has no `permission` field — visible to any authenticated user.
// The page itself filters cards/stats by direct area perms.

export const SIDE_NAV_ITEMS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'bar-chart-3',
    sectionTitle: 'Overview',
    path: 'dashboard',
  },
  {
    id: 'employees',
    label: 'Employees',
    sectionTitle: 'Employees',
    children: [
      {
        id: 'employees-add',
        label: 'Add Employee',
        icon: 'user',
        path: 'employees/add',
        permission: PERMISSIONS.EMPLOYEES_EDIT,
      },
      {
        id: 'employees-all',
        label: 'All Employees',
        icon: 'users',
        path: 'employees',
        permission: PERMISSIONS.EMPLOYEES_VIEW,
      },
      {
        id: 'employees-schedule',
        label: 'Work Schedule',
        icon: 'calendar',
        path: 'work-schedule',
        permission: PERMISSIONS.SCHEDULE_VIEW,
      },
      {
        id: 'employees-loans',
        label: 'Loans Management',
        icon: 'banknote',
        path: 'loans',
        permission: PERMISSIONS.LOANS_VIEW,
      },
    ],
  },
  {
    id: 'time-attendance',
    label: 'Time & Attendance',
    sectionTitle: 'Time & Attendance',
    children: [
      {
        id: 'time-tracking',
        label: 'Time Tracking',
        icon: 'clock',
        path: 'time-tracking',
        permission: PERMISSIONS.TIME_TRACKING_VIEW,
      },
      {
        id: 'ot-approval',
        label: 'OT Approval',
        icon: 'check-circle',
        path: 'ot-approval',
        permission: PERMISSIONS.TIME_OT_VIEW,
      },
      {
        id: 'kiosks',
        label: 'Kiosks',
        icon: 'monitor',
        path: 'kiosks',
        permission: PERMISSIONS.KIOSK_VIEW,
      },
    ],
  },
  {
    id: 'payroll',
    label: 'Payroll',
    sectionTitle: 'Payroll',
    children: [
      {
        id: 'payroll-adjustments',
        label: 'Adjustments',
        icon: 'file-pen',
        path: 'adjustments',
        permission: PERMISSIONS.ADJUSTMENTS_VIEW,
      },
      {
        id: 'payroll-process',
        label: 'Process Payroll',
        icon: 'wallet',
        path: 'payroll/process',
        permission: PERMISSIONS.PAYROLL_RUN,
      },
      {
        id: 'payroll-list',
        label: 'Payroll List',
        icon: 'clipboard-list',
        path: 'payroll',
        permission: PERMISSIONS.PAYROLL_VIEW,
      },
      {
        id: 'payroll-approval',
        label: 'Payroll Approval',
        icon: 'check-circle',
        path: 'payroll-approval',
        permission: PERMISSIONS.PAYROLL_APPROVE,
      },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'file-text',
    sectionTitle: 'Reports',
    path: 'reports',
    // Shown only to users who can open a report — see REPORTS_HUB_ANY above.
    anyPermission: REPORTS_HUB_ANY,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings',
    sectionTitle: 'Settings',
    path: 'settings',
    anyPermission: ANY_SETTINGS_PERMISSION,
  },
];

// ============================================================
// NAV FILTERING HELPER
// ============================================================

/**
 * Filter navigation items based on user permissions.
 * Returns a new array — never mutates SIDE_NAV_ITEMS.
 *
 * Items with no `permission` and no `anyPermission` are visible to any
 * authenticated user (e.g. the Dashboard).
 */
export const filterNavByPermissions = (userPermissions) => {
  if (!userPermissions) {
    return [];
  }

  const hasP = (p) => userPermissions.includes(p);
  const hasAnyP = (arr) => arr.some(p => userPermissions.includes(p));

  return SIDE_NAV_ITEMS.reduce((acc, item) => {
    // Single permission check
    if (item.permission && !hasP(item.permission)) {
      return acc;
    }

    // Any-of permission check (e.g., Settings visible with any settings perm)
    if (item.anyPermission && !hasAnyP(item.anyPermission)) {
      return acc;
    }

    // If item has children, filter them and clone
    if (item.children) {
      const filteredChildren = item.children.filter(child =>
        !child.permission || hasP(child.permission)
      );

      if (filteredChildren.length === 0) {
        return acc;
      }

      acc.push({ ...item, children: filteredChildren });
      return acc;
    }

    acc.push(item);
    return acc;
  }, []);
};

// ============================================================
// DRIFT GUARD — dev-mode assertion
// ============================================================
// Logs an error if PERMISSIONS (id constants) and PERMISSION_CATEGORIES
// (picker UI) fall out of sync. A permission missing from the picker is
// a silent UI omission; a picker key missing from PERMISSIONS crashes
// the editor at render time.
if (process.env.NODE_ENV !== 'production') {
  const flattened = new Set(
    PERMISSION_CATEGORIES.flatMap((c) => c.permissions.map((p) => p.key))
  );
  const taxonomy = new Set(Object.values(PERMISSIONS));
  const missingFromPicker = [...taxonomy].filter((k) => !flattened.has(k));
  const unknownInPicker = [...flattened].filter((k) => !taxonomy.has(k));
  if (missingFromPicker.length || unknownInPicker.length) {
    // eslint-disable-next-line no-console
    console.error(
      '[PermissionsDrift] PERMISSION_CATEGORIES vs PERMISSIONS mismatch',
      { missingFromPicker, unknownInPicker }
    );
  }
}
